import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { BusinessPartner } from '../model/business_partner';
import { environment } from '../../environments/environment';
import { ApiUrls } from '../model/api-urls';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { User } from '../model/user';
import { Driver } from '../model/driver';
import { AdminUserPayload, AdminUserResponse } from '../model/admin-user';
import { AzugaCarrierDto, Carrier, CarrierValidationResponse } from '../model/carrier';
import { MetricsTimezone } from '../model/metrics-timezone';
import {
  BusinessPartnerProviderConfig,
  BusinessPartnerProviderLink,
  Provider,
  ProviderConnectionTestResult,
  TestBusinessPartnerProviderPayload,
  UpsertBusinessPartnerProviderPayload
} from '../model/provider';

export type DriverMetadataFlags = {
  copilot_welcome?: boolean;
  copilot_disclosure?: boolean;
};

export type AzugaCarrierDriverDto = {
  id: number;
  driverId: number | null;
  alias: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  name: string | null;
  licenseNumber: string | null;
  enabledFeatures: string[];
  enabled: boolean;
  fleetSynced: boolean;
  username: string | null;
  language: string | null;
  truckId: string | null;
  RegisteredMobileDevices: number;
  Platform: string | null;
  AppVersion: string | null;
  MobileStateUpdatedAt: string | null;
};

export type AddAzugaDriverPayload = {
  BusinessPartnerId: number;
  ProviderId: 2;
  CarrierId: number;
  id: number;
  carrierId: number;
  enabledFeatures: string[];
  alias: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  username: string;
  password: string;
  language: 'ES' | 'EN';
  truckId: string | null;
};

export type AddAzugaDriverResponse = {
  ok: boolean;
  user_id?: string | null;
};

export type SetDriverTruckIdPayload =
  | { HOSUserName: string; truck_id: string | null }
  | {
      HOSUserName: string;
      truck_id: string | null;
      ProviderId: 2;
      BusinessPartnerId: number;
      CarrierId: number;
      DriverId: number;
    };

export type VersionPolicyLocalizedText = {
  en: string;
  es: string;
};

export type VersionPolicy = {
  minSupportedVersion: string;
  latestVersion: string;
  updateMode: string;
  title: VersionPolicyLocalizedText;
  message: VersionPolicyLocalizedText;
  ctaLabel: VersionPolicyLocalizedText;
  storeUrlIos: string;
  storeUrlAndroid: string;
};

@Injectable({
  providedIn: 'root'
})
export class CopilotApiService {

  constructor(private http: HttpClient) {}

  private getBaseUrl(): string {
    const base = environment.apiUrl;
    return base.endsWith('/') ? base.slice(0, -1) : base;
  }
  
  public getBusinessPartners(
    businessPartnerId: number = 1,
    options?: { expand?: 'providers' }
  ): Observable<BusinessPartner[]> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.BUSINESS_PARTNERS}`;
    const expandSuffix = options?.expand ? `&expand=${encodeURIComponent(options.expand)}` : '';
    const finalUrl = `${endpointUrl}?parent_bp_id=${businessPartnerId}${expandSuffix}`;
    return this.http.get<unknown>(finalUrl).pipe(
      map((response) => this.normalizeBusinessPartners(response))
    );
  }

  public getProviders(): Observable<Provider[]> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.PROVIDERS}`;
    return this.http.get<unknown>(endpointUrl).pipe(
      map((response) => this.normalizeProviders(response))
    );
  }

  public getBusinessPartnerProviders(businessPartnerId: number): Observable<BusinessPartnerProviderConfig[]> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.BUSINESS_PARTNER_PROVIDERS}`;
    const finalUrl = `${endpointUrl}?BusinessPartnerId=${encodeURIComponent(String(businessPartnerId))}`;
    return this.http.get<unknown>(finalUrl).pipe(
      map((response) => this.normalizeBusinessPartnerProviderConfigs(response))
    );
  }

  public addBusinessPartnerProvider(
    payload: UpsertBusinessPartnerProviderPayload & { BusinessPartnerId: number }
  ): Observable<BusinessPartnerProviderConfig> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.ADD_BUSINESS_PARTNER_PROVIDER}`;
    return this.http.post<unknown>(endpointUrl, payload, {
      headers: { 'Content-Type': 'application/json' }
    }).pipe(
      map((response) => this.requireBusinessPartnerProviderConfig(response))
    );
  }

  public updateBusinessPartnerProvider(
    payload: UpsertBusinessPartnerProviderPayload & { Id: number }
  ): Observable<BusinessPartnerProviderConfig> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.UPDATE_BUSINESS_PARTNER_PROVIDER}`;
    return this.http.post<unknown>(endpointUrl, payload, {
      headers: { 'Content-Type': 'application/json' }
    }).pipe(
      map((response) => this.requireBusinessPartnerProviderConfig(response))
    );
  }

  public deleteBusinessPartnerProvider(payload: { Id: number }): Observable<{ ok: boolean; id: number | null }> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.DELETE_BUSINESS_PARTNER_PROVIDER}`;
    return this.http.post<unknown>(endpointUrl, payload, {
      headers: { 'Content-Type': 'application/json' }
    }).pipe(
      map((response) => this.normalizeDeleteBusinessPartnerProviderResponse(response))
    );
  }

  public testBusinessPartnerProvider(
    payload: TestBusinessPartnerProviderPayload
  ): Observable<ProviderConnectionTestResult> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.TEST_BUSINESS_PARTNER_PROVIDER}`;
    return this.http.post<unknown>(endpointUrl, payload, {
      headers: { 'Content-Type': 'application/json' }
    }).pipe(
      map((response) => this.normalizeProviderConnectionTestResult(response))
    );
  }

  public addBusinessPartner(payload: BusinessPartner): Observable<BusinessPartner> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.ADD_BUSINESS_PARTNER}`;
    const body = {
      Name: payload.Name,
      LastName: payload.LastName,
      Phone: payload.Phone,
      Email: payload.Email,
      CompanyName: payload.CompanyName
    };
    return this.http.post<BusinessPartner>(endpointUrl, body, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // addCopilotAdminUser now sends JSON body
  public addAdminUser(payload: AdminUserPayload): Observable<AdminUserResponse> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.ADD_ADMIN_USER}`;
    return this.http.post<AdminUserResponse>(endpointUrl, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // Legacy: addDriverCopilot (x-www-form-urlencoded)
  public addDriver(driver: Driver & { ApiKey: string; CarrierId: string }): Observable<Driver> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.ADD_DRIVER}`;
    const body = new URLSearchParams({
      ApiKey: String(driver.ApiKey ?? ''),
      CarrierId: String(driver.CarrierId ?? ''),
      HOSDriverId: String(driver.HOSDriverId ?? ''),
      LastName: String(driver.LastName ?? ''),
      Name: String(driver.Name ?? ''),
      LicenseNumber: String(driver.LicenseNumber ?? ''),
      HOSUserName: String(driver.HOSUserName ?? ''),
      HOSHomeBaseId: String(driver.HOSHomeBaseId ?? '')
    });
    return this.http.post<Driver>(endpointUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  }

  // Legacy: enableDriverCopilot (x-www-form-urlencoded) - extended to carry add payload
  public enableDriver(params: {
    ApiKey: string;
    LastName: string;
    Name: string;
    HOSUserName: string;
    HOSDriverId: string;
    CarrierName?: string;
    LicenseNumber?: string;
    HOSHomeBaseId?: string;
  }): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.ENABLE_DRIVER}`;
    const body = new URLSearchParams({
      ApiKey: params.ApiKey,
      LastName: params.LastName,
      Name: params.Name,
      HOSUserName: params.HOSUserName,
      HOSDriverId: params.HOSDriverId,
      CarrierName: params.CarrierName ?? '',
      LicenseNumber: params.LicenseNumber ?? '',
      HOSHomeBaseId: params.HOSHomeBaseId ?? ''
    });
    return this.http.post(endpointUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  }

  // Legacy: disableDriverCopilot (x-www-form-urlencoded)
  public disableDriver(params: { ApiKey: string; LastName: string; Name: string; HOSUserName: string }): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.DISABLE_DRIVER}`;
    const body = new URLSearchParams({
      ApiKey: params.ApiKey,
      LastName: params.LastName,
      Name: params.Name,
      HOSUserName: params.HOSUserName
    });
    return this.http.post(endpointUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  }

  // Legacy: changeCopilotAdminUserStatus (x-www-form-urlencoded)
  public changeAdminUserStatus(params: { user_id: string; action: number }): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.CHANGE_ADMIN_USER_STATUS}`;
    const body = new URLSearchParams({
      user_id: params.user_id,
      action: String(params.action)
    });
    return this.http.post(endpointUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  }

  // validateCarrierCopilot (GET with ApiKey, BusinessPartner and BusinessPartnerId)
  public validateCarrier(params: { ApiKey: string; BusinessPartner: string; BusinessPartnerId: number }): Observable<CarrierValidationResponse> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.VALIDATE_CARRIER}`;
    const finalUrl = `${endpointUrl}?ApiKey=${encodeURIComponent(params.ApiKey)}&BusinessPartner=${encodeURIComponent(params.BusinessPartner)}&BusinessPartnerId=${encodeURIComponent(String(params.BusinessPartnerId))}`;
    return this.http.get<CarrierValidationResponse>(finalUrl);
  }

  // Legacy: enableCarrierCopilot (x-www-form-urlencoded)
  public enableCarrier(params: { CarrierId: number; BusinessPartnerId: number }): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.ENABLE_CARRIER}`;
    const body = new URLSearchParams({
      CarrierId: String(params.CarrierId),
      BusinessPartnerId: String(params.BusinessPartnerId)
    });
    return this.http.post(endpointUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      responseType: 'text' as const
    });
  }

  // Legacy: disableCarrierCopilot (x-www-form-urlencoded)
  public disableCarrier(params: { CarrierId: number; BusinessPartnerId: number }): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.DISABLE_CARRIER}`;
    const body = new URLSearchParams({
      CarrierId: String(params.CarrierId),
      BusinessPartnerId: String(params.BusinessPartnerId)
    });
    return this.http.post(endpointUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      responseType: 'text' as const
    });
  }

  // Fetch carriers (legacy list endpoint name inferred)
  public getCarriers(businessPartner: string = 'FleetBooster', businessPartnerId?: number | null): Observable<Carrier[]> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.CARRIERS}`;
    const businessPartnerIdSuffix = Number.isFinite(Number(businessPartnerId))
      ? `&BusinessPartnerId=${encodeURIComponent(String(Number(businessPartnerId)))}` : '';
    const finalUrl = `${endpointUrl}?BusinessPartner=${encodeURIComponent(businessPartner)}${businessPartnerIdSuffix}`;
    return this.http.get<unknown>(finalUrl).pipe(
      map((response) => this.normalizeCarriers(response))
    );
  }

  public getAzugaCarriers(params: { BusinessPartnerId: number; ProviderId: 2 }): Observable<AzugaCarrierDto[]> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.GET_AZUGA_CARRIERS}`;
    const finalUrl = `${endpointUrl}?BusinessPartnerId=${encodeURIComponent(String(params.BusinessPartnerId))}&ProviderId=${encodeURIComponent(String(params.ProviderId))}`;
    return this.http.get<unknown>(finalUrl).pipe(
      map((response) => this.normalizeAzugaCarriers(response))
    );
  }

  public getAzugaCarrierDrivers(params: { BusinessPartnerId: number; ProviderId: 2; CarrierId: number }): Observable<AzugaCarrierDriverDto[]> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.GET_AZUGA_CARRIER_DRIVERS}`;
    const finalUrl = `${endpointUrl}?BusinessPartnerId=${encodeURIComponent(String(params.BusinessPartnerId))}&ProviderId=${encodeURIComponent(String(params.ProviderId))}&CarrierId=${encodeURIComponent(String(params.CarrierId))}`;
    return this.http.get<unknown>(finalUrl).pipe(
      map((response) => this.normalizeAzugaCarrierDrivers(response))
    );
  }

  public addAzugaDriver(payload: AddAzugaDriverPayload): Observable<AddAzugaDriverResponse> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.ADD_AZUGA_DRIVER}`;
    return this.http.post<unknown>(endpointUrl, payload, {
      headers: { 'Content-Type': 'application/json' }
    }).pipe(
      map((response) => this.normalizeAddAzugaDriverResponse(response))
    );
  }

  public setAzugaCarrierStatus(params: { BusinessPartnerId: number; ProviderId: 2; CarrierId: number; Enabled: boolean }): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.SET_AZUGA_CARRIER_STATUS}`;
    const body = new URLSearchParams({
      BusinessPartnerId: String(params.BusinessPartnerId),
      ProviderId: String(params.ProviderId),
      CarrierId: String(params.CarrierId),
      Enabled: params.Enabled ? '1' : '0'
    });
    return this.http.post(endpointUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      responseType: 'text' as const
    });
  }

  public setAzugaDriverStatus(params: { BusinessPartnerId: number; ProviderId: 2; CarrierId: number; DriverId: number; Enabled: boolean }): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.SET_AZUGA_DRIVER_STATUS}`;
    const body = new URLSearchParams({
      BusinessPartnerId: String(params.BusinessPartnerId),
      ProviderId: String(params.ProviderId),
      CarrierId: String(params.CarrierId),
      DriverId: String(params.DriverId),
      Enabled: params.Enabled ? '1' : '0'
    });
    return this.http.post(endpointUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      responseType: 'text' as const
    });
  }

  // Legacy: getCarrierDriversCopilot (GET with ApiKey)
  public getCarrierDrivers(apiKey: string): Observable<Driver[]> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.CARRIER_DRIVERS}`;
    const finalUrl = `${endpointUrl}?ApiKey=${encodeURIComponent(apiKey)}`;
    return this.http.get<Driver[]>(finalUrl);
  }

  public getDriverTrucksMap(): Observable<Record<string, string | null>> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.DRIVER_TRUCKS_MAP}`;
    return this.http.get<unknown>(endpointUrl).pipe(
      map((response) => this.normalizeDriverTrucksMap(response))
    );
  }

  public getDriversMetadataMap(usernames?: string[]): Observable<Record<string, DriverMetadataFlags>> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.DRIVERS_METADATA_MAP}`;
    const validUsernames = Array.isArray(usernames)
      ? usernames.filter((username): username is string => typeof username === 'string' && username.trim().length > 0)
      : [];
    const querySuffix = validUsernames.length > 0
      ? `?${validUsernames
          .map((username) => `UserNames=${encodeURIComponent(username.trim())}`)
          .join('&')}`
      : '';
    return this.http.get<unknown>(`${endpointUrl}${querySuffix}`).pipe(
      map((response) => this.normalizeDriversMetadataMap(response))
    );
  }

  public getVersionPolicy(): Observable<VersionPolicy | null> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.GET_VERSION_POLICY}`;
    return this.http.get<unknown>(endpointUrl).pipe(
      map((response) => this.normalizeVersionPolicy(response))
    );
  }

  // Exports
  public exportRecentlyRegisteredDrivers(days: number, timezone: MetricsTimezone): Observable<HttpResponse<Blob>> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.EXPORT_RECENTLY_REGISTERED_DRIVERS}`;
    const finalUrl = `${endpointUrl}?days=${encodeURIComponent(days)}&timezone=${encodeURIComponent(timezone)}`;
    return this.http.get(finalUrl, { responseType: 'blob', observe: 'response' });
  }

  public exportAcceptedOptimizations(timezone: MetricsTimezone): Observable<HttpResponse<Blob>> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.EXPORT_ACCEPTED_OPTIMIZATIONS}`;
    const finalUrl = `${endpointUrl}?timezone=${encodeURIComponent(timezone)}`;
    return this.http.get(finalUrl, { responseType: 'blob', observe: 'response' });
  }

  public exportOptimizationOpportunities(
    days: number,
    includeNotifications: boolean,
    includeViolations: boolean,
    timezone: MetricsTimezone
  ): Observable<HttpResponse<Blob>> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.EXPORT_OPTIMIZATION_OPPORTUNITIES}`;
    const finalUrl = `${endpointUrl}?days=${encodeURIComponent(days)}&IncludeNotifications=${encodeURIComponent(includeNotifications)}&IncludeViolations=${encodeURIComponent(includeViolations)}&timezone=${encodeURIComponent(timezone)}`;
    return this.http.get(finalUrl, { responseType: 'blob', observe: 'response' });
  }

  public getAdminUsers(
    name: string,
    businessPartner: string = 'FleetBooster',
    parent: string = 'FleetBooster',
    level: number = 0
  ): Observable<User[]> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.ADMIN_USERS}`
    const finalUrl = `${endpointUrl}?name=${encodeURIComponent(name)}&businessPartner=${encodeURIComponent(businessPartner)}&parent=${encodeURIComponent(parent)}&level=${encodeURIComponent(level)}`;
    return this.http.get<User[]>(finalUrl);
  }

  public updateBusinessPartner(payload: { Id: number; Enabled: boolean }): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.UPDATE_BUSINESS_PARTNER}`;
    return this.http.post(endpointUrl, payload, {
      headers: { 'Content-Type': 'application/json' },
      observe: 'response'
    });
  }

  public updateBusinessPartnerDetails(payload: { Id: number; Name: string; LastName: string; Email: string; Phone: string; CompanyName: string }): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.UPDATE_BUSINESS_PARTNER}`;
    return this.http.post(endpointUrl, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // resetLogPointerForDriver (JSON)
  public resetLogPointerForDriver(params: { Name: string; LastName: string; DaysAgo: number }): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.RESET_LOG_POINTER_FOR_DRIVER}`;
    return this.http.post(endpointUrl, params, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // setCopilotDriverPassword (JSON)
  public setDriverPassword(params: { HOSUserName: string; Password: string }): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.SET_DRIVER_PASSWORD}`;
    return this.http.post(endpointUrl, params, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // setCopilotDriverTruckId (JSON)
  public setDriverTruckId(params: SetDriverTruckIdPayload): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.SET_DRIVER_TRUCK_ID}`;
    return this.http.post(endpointUrl, params, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // clearDriverFlags (JSON)
  public clearDriverFlags(params: { clearWelcome: boolean; clearDisclosure: boolean; HOSUserName: string }): Observable<unknown> {
    const endpointUrl = `${this.getBaseUrl()}/${ApiUrls.CLEAR_DRIVER_FLAGS}`;
    return this.http.post(endpointUrl, params, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  private normalizeDriverTrucksMap(value: unknown): Record<string, string | null> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    const normalized: Record<string, string | null> = {};
    for (const [rawKey, rawValue] of Object.entries(value as Record<string, unknown>)) {
      if (typeof rawKey !== 'string' || rawKey.length === 0) {
        continue;
      }
      if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim();
        normalized[rawKey] = trimmed.length > 0 ? trimmed : null;
        continue;
      }
      normalized[rawKey] = null;
    }

    return normalized;
  }

  private normalizeDriversMetadataMap(value: unknown): Record<string, DriverMetadataFlags> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    const normalized: Record<string, DriverMetadataFlags> = {};
    for (const [rawKey, rawValue] of Object.entries(value as Record<string, unknown>)) {
      if (typeof rawKey !== 'string' || rawKey.length === 0) {
        continue;
      }
      if (!rawValue || typeof rawValue !== 'object' || Array.isArray(rawValue)) {
        continue;
      }
      const candidate = rawValue as Record<string, unknown>;
      normalized[rawKey] = {
        copilot_welcome: typeof candidate['copilot_welcome'] === 'boolean' ? candidate['copilot_welcome'] : undefined,
        copilot_disclosure: typeof candidate['copilot_disclosure'] === 'boolean' ? candidate['copilot_disclosure'] : undefined
      };
    }

    return normalized;
  }

  private normalizeBusinessPartners(value: unknown): BusinessPartner[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const normalized: BusinessPartner[] = [];
    for (const row of value) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        continue;
      }

      const candidate = row as Record<string, unknown>;
      const rawProviders = candidate['providers'] ?? candidate['Providers'];
      normalized.push({
        Id: this.toNullableNumber(candidate['Id']),
        ParentId: this.toNullableNumber(candidate['ParentId']),
        Name: this.toStringValue(candidate['Name']),
        LastName: this.toStringValue(candidate['LastName']),
        Phone: this.toStringValue(candidate['Phone']),
        Email: this.toStringValue(candidate['Email']),
        CompanyName: this.toStringValue(candidate['CompanyName']),
        Level: this.toNumber(candidate['Level'], 0),
        Enabled: this.toBoolean(candidate['Enabled']),
        CreatedBy: this.toNullableString(candidate['CreatedBy']),
        CreatedDate: this.toNullableString(candidate['CreatedDate']),
        providers: this.normalizeBusinessPartnerProviders(rawProviders)
      });
    }

    return normalized;
  }

  private normalizeProviders(value: unknown): Provider[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const normalized: Provider[] = [];
    for (const row of value) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        continue;
      }
      const candidate = row as Record<string, unknown>;
      const id = this.toNullableNumber(candidate['Id']);
      const name = this.toStringValue(candidate['Name']);
      if (id == null || name.length === 0) {
        continue;
      }
      normalized.push({ Id: id, Name: name });
    }

    return normalized;
  }

  private normalizeBusinessPartnerProviders(value: unknown): BusinessPartnerProviderLink[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const normalized: BusinessPartnerProviderLink[] = [];
    for (const row of value) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        continue;
      }

      const candidate = row as Record<string, unknown>;
      const id = this.toNullableNumber(candidate['Id']);
      const bpId = this.toNullableNumber(candidate['BusinessPartnerId']);
      const providerId = this.toNullableNumber(candidate['ProviderId']);
      const providerName = this.toStringValue(candidate['ProviderName']);
      if (id == null || bpId == null || providerId == null || providerName.length === 0) {
        continue;
      }

      normalized.push({
        Id: id,
        BusinessPartnerId: bpId,
        ProviderId: providerId,
        ProviderName: providerName,
        CreatedDate: this.toNullableString(candidate['CreatedDate'])
      });
    }

    return normalized;
  }

  private normalizeBusinessPartnerProviderConfigs(value: unknown): BusinessPartnerProviderConfig[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const normalized: BusinessPartnerProviderConfig[] = [];
    for (const row of value) {
      const config = this.normalizeBusinessPartnerProviderConfig(row);
      if (config !== null) {
        normalized.push(config);
      }
    }
    return normalized;
  }

  private normalizeBusinessPartnerProviderConfig(value: unknown): BusinessPartnerProviderConfig | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    const candidate = value as Record<string, unknown>;
    const id = this.toNullableNumber(candidate['Id']);
    const businessPartnerId = this.toNullableNumber(candidate['BusinessPartnerId']);
    const providerId = this.toNullableNumber(candidate['ProviderId']);
    const providerType = this.toStringValue(candidate['ProviderType']);
    const url = this.toStringValue(candidate['Url']);

    if (id == null || businessPartnerId == null || providerId == null || providerType.length === 0 || url.length === 0) {
      return null;
    }

    return {
      Id: id,
      BusinessPartnerId: businessPartnerId,
      ProviderId: providerId,
      ProviderType: providerType,
      Url: url,
      ApiKey: this.toNullableString(candidate['ApiKey']),
      ApiKeyConfigured: this.toBoolean(candidate['ApiKeyConfigured']),
      Configured: this.toBoolean(candidate['Configured']),
      Username: this.toNullableString(candidate['Username'])
    };
  }

  private normalizeDeleteBusinessPartnerProviderResponse(value: unknown): { ok: boolean; id: number | null } {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return { ok: false, id: null };
    }

    const candidate = value as Record<string, unknown>;
    return {
      ok: this.toBoolean(candidate['ok']),
      id: this.toNullableNumber(candidate['id'])
    };
  }

  private normalizeProviderConnectionTestResult(value: unknown): ProviderConnectionTestResult {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return { ok: false };
    }

    const candidate = value as Record<string, unknown>;
    const reason = candidate['reason'];
    return {
      ok: this.toBoolean(candidate['ok']),
      reason: typeof reason === 'string' && reason.length > 0 ? reason : undefined
    };
  }

  private requireBusinessPartnerProviderConfig(value: unknown): BusinessPartnerProviderConfig {
    const normalized = this.normalizeBusinessPartnerProviderConfig(value);
    if (normalized !== null) {
      return normalized;
    }
    throw new Error('Invalid business partner provider config response');
  }

  private toNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private toNullableNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === '1' || normalized === 'true';
    }
    return false;
  }

  private toStringValue(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNullableString(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private normalizeVersionPolicy(value: unknown): VersionPolicy | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    const candidate = value as Record<string, unknown>;
    const minSupportedVersion = this.normalizeNonEmptyString(candidate['minSupportedVersion']);
    const latestVersion = this.normalizeNonEmptyString(candidate['latestVersion']);
    const updateMode = this.normalizeNonEmptyString(candidate['updateMode']);
    const title = this.normalizeLocalizedText(candidate['title']);
    const message = this.normalizeLocalizedText(candidate['message']);
    const ctaLabel = this.normalizeLocalizedText(candidate['ctaLabel']);
    const storeUrlIos = this.normalizeNonEmptyString(candidate['storeUrlIos']);
    const storeUrlAndroid = this.normalizeNonEmptyString(candidate['storeUrlAndroid']);

    if (
      minSupportedVersion === null
      || latestVersion === null
      || updateMode === null
      || title === null
      || message === null
      || ctaLabel === null
      || storeUrlIos === null
      || storeUrlAndroid === null
    ) {
      return null;
    }

    return {
      minSupportedVersion,
      latestVersion,
      updateMode,
      title,
      message,
      ctaLabel,
      storeUrlIos,
      storeUrlAndroid
    };
  }

  private normalizeLocalizedText(value: unknown): VersionPolicyLocalizedText | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    const candidate = value as Record<string, unknown>;
    const en = this.normalizeNonEmptyString(candidate['en']);
    const es = this.normalizeNonEmptyString(candidate['es']);
    if (en === null || es === null) {
      return null;
    }

    return { en, es };
  }

  private normalizeNonEmptyString(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private normalizeCarriers(value: unknown): Carrier[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const normalized: Carrier[] = [];
    for (const row of value) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        continue;
      }
      const candidate = row as Record<string, unknown>;
      const id = this.toNullableNumber(candidate['Id']);
      const businessPartnerId = this.toNullableNumber(candidate['BusinessPartnerId']);
      const apiKey = this.toStringValue(candidate['ApiKey']).trim();
      const name = this.toStringValue(candidate['Name']).trim();
      const providerId = this.toNullableNumber(candidate['ProviderId']);
      const providerName = this.toStringValue(candidate['ProviderName']).trim();
      if (id == null || businessPartnerId == null || providerId == null || apiKey.length === 0 || name.length === 0 || providerName.length === 0) {
        continue;
      }

      normalized.push({
        Id: id,
        BusinessPartnerId: businessPartnerId,
        ApiKey: apiKey,
        Name: name,
        Enabled: this.toBoolean(candidate['Enabled']),
        ProviderId: providerId,
        ProviderName: providerName
      });
    }

    return normalized;
  }

  private normalizeAzugaCarriers(value: unknown): AzugaCarrierDto[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const normalized: AzugaCarrierDto[] = [];
    for (const row of value) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        continue;
      }

      const candidate = row as Record<string, unknown>;
      const rawId = candidate['id'];
      if (rawId == null) {
        continue;
      }
      const id = this.toNullableNumber(rawId);
      const name = this.toStringValue(candidate['carrierName']).trim();
      if (id == null || name.length === 0) {
        continue;
      }

      normalized.push({
        id,
        accountId: this.toNullableNumber(candidate['accountId']),
        carrierName: name,
        carrierDotNumber: this.toNullableString(candidate['carrierDotNumber']),
        street: this.toNullableString(candidate['street']),
        city: this.toNullableString(candidate['city']),
        state: this.toNullableString(candidate['state']),
        country: this.toNullableString(candidate['country']),
        zip: this.toNullableString(candidate['zip']),
        phoneNumber: this.toNullableString(candidate['phoneNumber']),
        timezone: this.toNullableString(candidate['timezone']),
        startTimeOfDay: this.toNullableString(candidate['startTimeOfDay']),
        isDefault: this.toBoolean(candidate['isDefault']),
        enabled: this.toBoolean(candidate['enabled'])
      });
    }

    return normalized;
  }

  private normalizeAzugaCarrierDrivers(value: unknown): AzugaCarrierDriverDto[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const normalized: AzugaCarrierDriverDto[] = [];
    for (const row of value) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        continue;
      }

      const candidate = row as Record<string, unknown>;
      const id = this.toNullableNumber(
        candidate['id'] ?? candidate['driverId'] ?? candidate['hosDriverId'] ?? candidate['externalDriverId']
      );
      if (id == null) {
        continue;
      }

      normalized.push({
          id,
          driverId: this.toNullableNumber(candidate['driverId']),
          alias: this.toNullableString(candidate['alias']),
          email: this.toNullableString(candidate['email']),
          firstName: this.toNullableString(candidate['firstName']),
          lastName: this.toNullableString(candidate['lastName']),
          name: this.toNullableString(candidate['name']),
          licenseNumber: this.toNullableString(candidate['licenseNumber']),
          enabledFeatures: this.toStringArray(candidate['enabledFeatures']),
          enabled: this.toBoolean(candidate['enabled']),
          fleetSynced: this.toBoolean(candidate['fleetSynced']),
          username: this.toNullableString(candidate['username']),
        language: this.toNullableString(candidate['language']),
        truckId: this.toNullableString(candidate['truckId']),
        RegisteredMobileDevices: this.normalizeNonNegativeInteger(candidate['RegisteredMobileDevices']),
        Platform: this.toNullableString(candidate['Platform']),
        AppVersion: this.toNullableString(candidate['AppVersion']),
        MobileStateUpdatedAt: this.toNullableString(candidate['MobileStateUpdatedAt'])
      });
    }

    return normalized;
  }

  private normalizeAddAzugaDriverResponse(value: unknown): AddAzugaDriverResponse {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return { ok: false, user_id: null };
    }

    const candidate = value as Record<string, unknown>;
    return {
      ok: this.toBoolean(candidate['ok']),
      user_id: this.toNullableString(candidate['user_id'])
    };
  }

  private normalizeNonNegativeInteger(value: unknown): number {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      return 0;
    }
    return Math.floor(numericValue);
  }

  private toStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }
}
